import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { QUERY_KEYS, STALE_TIME } from '@/libs/constants'
import type {
  HookParams,
  PackageDeal,
  PackageDealListParams,
  PackageDealParams,
} from '@/libs/interfaces'
import { packageDealsApi } from '@/services/api'

export function useInfinitePackageDeals({ params, enabled }: HookParams<PackageDealListParams>) {
  return useInfiniteQuery({
    queryKey: QUERY_KEYS.packageDeals.infinite(params),
    queryFn: ({ pageParam }) =>
      packageDealsApi.listCursor({
        ...params,
        cursor: pageParam,
      }),
    getNextPageParam: (lastPage) => lastPage.pageInfo.next || undefined,
    getPreviousPageParam: (firstPage) => firstPage.pageInfo.prev || undefined,
    enabled: (enabled ?? true) && !!params.orgID && !!params.storeID,
    initialPageParam: undefined as string | undefined,
    staleTime: STALE_TIME['24Hours'],
  })
}

export function usePackageDeals({ params, enabled }: HookParams<PackageDealListParams>) {
  return useQuery<PackageDeal[]>({
    queryKey: QUERY_KEYS.packageDeals.list(params),
    queryFn: () => packageDealsApi.list(params),
    enabled: (enabled ?? true) && !!params.orgID && !!params.storeID,
    staleTime: STALE_TIME['24Hours'],
  })
}

export function usePackageDealByID({ params, enabled }: HookParams<PackageDealParams>) {
  return useQuery<Partial<PackageDeal>>({
    queryKey: QUERY_KEYS.packageDeals.id(params.packageID),
    queryFn: () => packageDealsApi.getByID(params),
    enabled: (enabled ?? true) && !!params.packageID && !!params.orgID && !!params.storeID,
    staleTime: STALE_TIME['24Hours'],
  })
}

export function useCreatePackageDeal() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: packageDealsApi.create,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.packageDeals.all,
      })
    },
    onError: () => {
      toast.error(t('common.error'))
    },
  })
}

export function useUpdatePackageDeal() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: packageDealsApi.update,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.packageDeals.all,
      })
    },
    onError: () => {
      toast.error(t('common.error'))
    },
  })
}

export function useDeletePackageDeal() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: packageDealsApi.delete,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.packageDeals.all,
      })
    },
    onError: () => {
      toast.error(t('common.error'))
    },
  })
}
